var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import throttle from 'lodash.throttle';
import { injectable, inject, optional, symbolToken } from '@servicetitan/react-ioc';
import { defaultLogConfig } from './log-config';
import { LogStorage } from './log-storage';
import { serializeWithMetadata } from './serialize-with-metadata';
export { LOG_CONFIG_TOKEN } from './log-config';
export var LogEntryType;
(function (LogEntryType) {
    LogEntryType["Info"] = "Info";
    LogEntryType["Warning"] = "Warning";
    LogEntryType["Error"] = "Error";
})(LogEntryType || (LogEntryType = {}));
export const LOG_PARENT_TOKEN = symbolToken('LOG_PARENT_TOKEN');
let Log = class Log {
    constructor(logStorage, parent) {
        Object.defineProperty(this, "logStorage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: logStorage
        });
        Object.defineProperty(this, "parent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: parent
        });
        Object.defineProperty(this, "errorLogHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.validate();
        this.errorLogHandler = new ErrorLogHandler(this, this.logStorage);
    }
    info(entry) {
        var _a;
        (_a = this.parent) === null || _a === void 0 ? void 0 : _a.info(entry);
        return this.store(this.expand(LogEntryType.Info, entry));
    }
    warning(entry) {
        var _a;
        (_a = this.parent) === null || _a === void 0 ? void 0 : _a.warning(entry);
        return this.store(this.expand(LogEntryType.Warning, entry));
    }
    error(entry) {
        var _a;
        (_a = this.parent) === null || _a === void 0 ? void 0 : _a.error(entry);
        // eslint-disable-next-line no-console
        console.error('@log-service/error', entry);
        return this.errorLogHandler.handle(this.expandError(entry)); // with errorLogHandler throttling
    }
    store(entry) {
        return this.logStorage.store(entry);
    }
    expand(type, entry) {
        const data = Object.assign(Object.assign({}, entry.data), this.dataDefault());
        return Object.assign(Object.assign({ type, category: null, message: null, code: null }, entry), { error: null, data });
    }
    expandError(entry) {
        let error;
        if (entry.error instanceof Error) {
            error = entry.error;
        }
        else {
            error = new Error(serializeWithMetadata(entry.error));
        }
        const _a = this.expand(LogEntryType.Error, entry), { message } = _a, rest = __rest(_a, ["message"]);
        return Object.assign(Object.assign({ message: message !== null && message !== void 0 ? message : error.message }, rest), { error });
    }
    dataDefault() {
        return Object.assign({ CreatedOn: new Date(), Online: window.navigator.onLine, Url: window.location.toString(), UserAgent: window.navigator.userAgent, Source: 'Frontend', Plugins: plugins() }, timezoneInfo());
    }
    validate() {
        if (!this.parent) {
            return;
        }
        const { endpoint, storageKey } = this.logStorage.config;
        if (endpoint === defaultLogConfig.endpoint || storageKey === defaultLogConfig.storageKey) {
            throw new Error('nested Log cannot use default configuration');
        }
    }
};
Log = __decorate([
    injectable(),
    __param(0, inject(LogStorage)),
    __param(1, inject(LOG_PARENT_TOKEN)),
    __param(1, optional()),
    __metadata("design:paramtypes", [LogStorage,
        Log])
], Log);
export { Log };
class ErrorLogHandler {
    constructor(logService, logStorage) {
        Object.defineProperty(this, "logService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "logStorage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "logTable", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new HashTable()
        });
        Object.defineProperty(this, "repetitiveLogsCache", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new HashTable()
        });
        Object.defineProperty(this, "cacheFlushDuration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 30000
        });
        Object.defineProperty(this, "throttleLogsDuration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 2000
        });
        Object.defineProperty(this, "flushThrottled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: throttle(() => this.flush(), this.throttleLogsDuration, {
                leading: false,
                trailing: true,
            })
        });
        this.logService = logService;
        this.logStorage = logStorage;
        setInterval(() => this.repetitiveLogsCache.clear(), this.cacheFlushDuration);
    }
    handle(entry) {
        this.add(entry);
        this.flushThrottled();
    }
    add(entry) {
        const key = this.keyFor(entry);
        if (this.repetitiveLogsCache.get(key)) {
            /* error is a repetitive error and has been reported, we don't need to report it again */
            return;
        }
        if (this.logTable.get(key) == null) {
            this.logTable.set(key, entry);
        }
        else {
            /* present in entry table, hence a duplicate, don't set, rather increase count */
            const entry = this.logTable.get(key);
            if (typeof entry.data.count === 'number') {
                entry.data.count++;
            }
            else {
                entry.data.count = 2;
            }
            this.logTable.set(key, entry);
        }
    }
    keyFor(entry) {
        var _a;
        return {
            message: entry.error.message,
            stack: (_a = entry.error.stack) !== null && _a !== void 0 ? _a : '',
        };
    }
    flush() {
        const hashes = this.logTable.getHashes();
        for (const key of hashes) {
            const entry = this.logTable.get(key);
            if ('count' in entry.data) {
                /* insert into cache if it is a repetitive error */
                this.repetitiveLogsCache.set(key, true);
            }
            this.logStorage.store(entry);
        }
        this.logTable.clear();
    }
}
class HashTable {
    constructor() {
        Object.defineProperty(this, "hashes", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
    }
    set(key, value) {
        this.hashes[JSON.stringify(key)] = value;
    }
    get(key) {
        return this.hashes[JSON.stringify(key)];
    }
    getHashes() {
        return Object.keys(this.hashes).map(value => JSON.parse(value));
    }
    clear() {
        this.hashes = {};
    }
}
function plugins() {
    try {
        return Array.prototype.slice
            .call(window.navigator.plugins)
            .map(p => [p.name, p.filename, p.description].join(','))
            .join('/');
    }
    catch (e) {
        return 'unknown';
    }
}
function timezoneInfo() {
    // https://stackoverflow.com/questions/11887934/how-to-check-if-dst-daylight-saving-time-is-in-effect-and-if-so-the-offset
    const now = new Date();
    const jan = new Date(now.getFullYear(), 0, 1);
    const jul = new Date(now.getFullYear(), 6, 1);
    const TzOffset = now.getTimezoneOffset();
    const IsDst = TzOffset < Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
    const IsDstSubject = jan.getTimezoneOffset() !== jul.getTimezoneOffset();
    return {
        TzOffset,
        IsDst,
        IsDstSubject,
    };
}
//# sourceMappingURL=log.js.map